import {Box} from "@mui/material";
import {ColumnDef} from "@tanstack/react-table";
import type {AggregationRateListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {CommonAggregationService} from "crqlar-react-common/dist/services/aggregation/commonAggregation.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatDateDMYHM, formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {getAmountFormatted, getSymbolForCurrency} from "crqlar-react-common/dist/utils/currency.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

// mirrors the legacy amount/change rendering: own price, or parent price + absolute/percentage change
const renderRateAmount = (rate: AggregationRateListItem) => {
    if (!rate.parentRate) return getAmountFormatted(rate.price);

    const parentPriceAmount = rate.parentRate.price ? getAmountFormatted(rate.parentRate.price) : '-';
    if (rate.price) return getAmountFormatted(rate.price);
    if (rate.rateChange?.totalAmountChangeCent) {
        const isIncrease = rate.rateChange.totalAmountChangeCent > 0;
        return `${parentPriceAmount} / ${isIncrease ? '+' : ''}${rate.rateChange.totalAmountChangeCent / 100} ${getSymbolForCurrency(rate.parentRate.price?.currency)}`;
    }
    if (rate.rateChange?.totalPercentageChange) {
        const isIncrease = rate.rateChange.totalPercentageChange > 0;
        return `${parentPriceAmount} / ${isIncrease ? '+' : ''} ${rate.rateChange.totalPercentageChange}%`;
    }
    return '';
};

const RatesList = () => {
    useTrackScreenVisit('system rates');

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<AggregationRateListItem>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: rate => rate.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 240,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'baseRate',
            header: t('Base rate'),
            accessorFn: rate => rate.parentRate?.name,
            cell: ({row}) => <Box>{row.original.parentRate?.name ?? '-'}</Box>,
            size: 180,
            meta: {defaultActive: true}
        },
        {
            id: 'amount',
            header: t('Amount/Change'),
            accessorFn: rate => rate.price?.amountCents,
            cell: ({row}) => <Box>{renderRateAmount(row.original)}</Box>,
            size: 180,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'price.amountCents'}
        },
        {
            id: 'timeLimit',
            header: t('Time limit'),
            accessorFn: rate => rate.endTime,
            cell: ({row}) => {
                const {startTime, endTime} = row.original;
                if (!startTime && !endTime) return <Box>{t('Always active')}</Box>;
                if (startTime && endTime) return <Box>{`${formatDateDMYHM(startTime)} - ${formatDateDMYHM(endTime)}`}</Box>;
                if (startTime) return <Box>{`from ${formatDateDMYHM(startTime)}`}</Box>;
                if (endTime) return <Box>{`until ${formatDateDMYHM(endTime)}`}</Box>;
                return <Box>-</Box>;
            },
            size: 260,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'endTime'}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: rate => rate.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: rate => rate.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-rates',
        columns,
        getRowId: rate => rate.id,
        onRowClick: rate => navigate(`/system/rates/${rate.id}`),
        initialSorting: [{id: 'name', desc: false}],
        queryFn: ({pagination, searchQuery, sorting}) => CommonAggregationService.listRates({
            name: searchQuery ? [searchQuery] : undefined,
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: sorting ? [sorting] : undefined
            }
        })
    });

    return (
        <SettingsListPage title={t('Rates / Prices')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create Rate')}
                            onClick={() => navigate('/system/rates/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default RatesList;
